import type Shepherd from 'shepherd.js';
import { makeLog } from 'shared/utils/log';
import {
  resetTourCall,
  resetTourTakeover,
  setCallStatus,
  setIsTouring,
  setTakeoverStatus,
  setTourStepId,
  startTourDemoIframe,
  syncOrgWithTourDemoIframe,
} from 'shared/store/tour-slice';
import { makeFakeTourClient } from 'shared/store/data-util';
import { history, AppDispatch } from 'shared/store';
import type { ClassAttributeType } from 'types';
import { mediaDevices } from './media-devices';

const log = makeLog('utils/tour');

type ShepherdStep = ClassAttributeType<Shepherd.Step, 'options'> & {
  path?: string;
  requiresClickTarget?: boolean;
  skipHistory?: boolean;
};

export enum TourStepId {
  Start = 'start',
  VisitorsList = 'visitors-list',
  ExampleVisitorCard = 'example-visitor-card',
  VisitorDisplay = 'visitor-display',
  StartCall = 'start-call',
  StartTakeover = 'start-takeover',
  EndCall = 'end-call',
  Final = 'final',
}

export enum TourElementId {
  StartModal = 'StartModal',
  Sidebar = 'Sidebar',
  VisitorInfo = 'VisitorInfo',
  VisitorCard = 'VisitorCard',
  VisitorDisplayFrame = 'VisitorDisplayFrame',
  VideoCallButton = 'VideoCallButton',
  TakeoverButton = 'TakeoverButton',
  FinishModal = 'FinishModal',
}

export const DEMO_VIDEOS = {
  idle: {
    src: 'https://uploads.servicebell.com/video/onboarding/will-idle.mp4',
    duration: 20.615 * 1000,
  },
  callStart: {
    src: 'https://uploads.servicebell.com/video/onboarding/will-call-start.mp4',
    duration: 13.851 * 1000,
  },
  acceptTakeover: {
    src: 'https://uploads.servicebell.com/video/onboarding/will-accept-takeover.mp4',
    duration: 10.535 * 1000,
  },
} as const;

let activeTour: Shepherd.Tour | undefined;

export function getActiveTour() {
  return activeTour;
}

export async function startTour(dispatch: AppDispatch, initialStep?: TourStepId) {
  if (activeTour) {
    log.warn('Tried to start tour while a tour is currently active');
    return activeTour;
  }

  // Simultaneously import the library and styles asynchronously. The styles
  // don't need proper typing so they're ts-ignore'd.
  const [ShepherdLib] = await Promise.all([
    import('shepherd.js'),
    /* @ts-ignore */
    import('../styles/shepherd.less'),
  ]);

  const onStepShow = () => {
    const currentStep = activeTour?.getCurrentStep();
    if (currentStep) {
      dispatch(setTourStepId(currentStep.id as TourStepId));
      const skipHistory = !!steps[currentStep.id as TourStepId]?.skipHistory;
      if (!skipHistory) {
        history.replace(`${window.location.pathname}?tourStep=${currentStep.id}`);
      }
    }
    makeProgressIndicator();
  };

  // Initialize the tour
  activeTour = new ShepherdLib.default.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      modalOverlayOpeningPadding: 0,
      modalOverlayOpeningRadius: 16,
      popperOptions: {
        modifiers: [
          // Space popups further from the highlighted area
          { name: 'offset', options: { offset: [0, 24] } },
          // Fix arrow centering
          {
            name: 'arrow',
            options: { padding: ({ popper }: any) => popper.height / 2 },
          },
        ],
      },
      when: {
        show: onStepShow,
      },
    },
  });

  // Pre-generate the ID of the fake user for the tour.
  const fakeVisitorId = makeFakeTourClient().sbid;

  // Define the steps of the tour. Define as a dictionary for easy reference and ordering.
  const steps: Record<TourStepId, ShepherdStep> = {
    [TourStepId.Start]: {
      id: TourStepId.Start,
      path: '/',
      attachTo: {
        element: TourElementId.StartModal,
      },
      requiresClickTarget: true,
      when: {
        show: () => {
          document
            .querySelector('.shepherd-element')
            ?.classList.add('shepherd-hide-element');
        },
        hide: () => {
          document
            .querySelector('.shepherd-element')
            ?.classList.remove('shepherd-hide-element');
        },
      },
    },
    [TourStepId.VisitorsList]: {
      id: TourStepId.VisitorsList,
      path: '/',
      title: 'Live Visitor Feed',
      text: `Once you’ve installed the ServiceBell tag on your site, visitors will appear in this list.`,
      attachTo: {
        element: TourElementId.Sidebar,
        on: 'right-start',
      },
      modalOverlayOpeningPadding: 16,
    },
    [TourStepId.ExampleVisitorCard]: {
      id: TourStepId.ExampleVisitorCard,
      path: '/',
      title: 'First Visitor',
      text:
        'Oh look, your first visitor! Try <em>clicking on them</em> to join their session.',
      requiresClickTarget: true,
      attachTo: {
        element: TourElementId.Sidebar,
        on: 'right-start',
      },
      beforeShowPromise: () => {
        return new Promise(resolve => {
          // Delay this event by a bit to give the card some time to render in the list.
          dispatch(setTourStepId(TourStepId.ExampleVisitorCard));
          setTimeout(resolve, 100);
        });
      },
      modalOverlayOpeningPadding: 16,
    },
    [TourStepId.VisitorDisplay]: {
      id: TourStepId.VisitorDisplay,
      path: `/visitor/${fakeVisitorId}`,
      title: 'Live Session',
      text: `
      You’ll be able to see exactly what they’re seeing. Take your time, they won’t be alerted that you’ve joined yet.`,
      attachTo: {
        element: TourElementId.VisitorDisplayFrame,
        on: 'left',
      },
      beforeShowPromise: () => {
        return new Promise(resolve => {
          setTimeout(resolve, 500);
        });
      },
      when: {
        show: () => {
          onStepShow();
          dispatch(startTourDemoIframe());
        },
      },
    },

    [TourStepId.StartCall]: {
      id: TourStepId.StartCall,
      path: `/visitor/${fakeVisitorId}`,
      title: 'Live Video Call',

      text: `
        Looks like they are interested in purchasing your product, now would be a good time to engage.
        <br />
        <br />
        Click the “Video” button to initiate a call to the visitor. They’ll need to accept before they can hear you. 
        <br />
        <br />
        Don’t worry, you’re not calling a real person in this demo 😅
      `,
      requiresClickTarget: true,
      skipHistory: true,
      attachTo: {
        element: TourElementId.VisitorDisplayFrame,
        on: 'left-start',
      },
      when: {
        show: () => {
          document.body.classList.remove('shepherd-hide-overlay');
          onStepShow();
          dispatch(resetTourCall());
          dispatch(syncOrgWithTourDemoIframe());
        },
      },
    },
    [TourStepId.StartTakeover]: {
      id: TourStepId.StartTakeover,
      path: `/visitor/${fakeVisitorId}`,
      title: 'Screen Control',
      text: `
        <em>Click the “Control” button</em> to begin a a screen takeover. Your cursor will appear on their screen, and you can click and scroll on the page.
      `,
      requiresClickTarget: true,
      skipHistory: true,
      attachTo: {
        element: TourElementId.TakeoverButton,
        on: 'left',
      },
      when: {
        show: () => {
          document.body.classList.add('shepherd-hide-overlay');
          onStepShow();
          dispatch(resetTourTakeover());
        },
      },
    },
    [TourStepId.EndCall]: {
      id: TourStepId.EndCall,
      path: `/visitor/${fakeVisitorId}`,
      title: 'End Call',
      text: `Click the “End button when you’re ready to end the call.`,
      requiresClickTarget: true,
      skipHistory: true,
      attachTo: {
        element: TourElementId.VideoCallButton,
        on: 'right',
      },
      when: {
        show: () => {
          document.body.classList.add('shepherd-hide-overlay');
          onStepShow();
          dispatch(setTakeoverStatus('active'));
          dispatch(setCallStatus('active'));
        },
        hide: () => {
          document.body.classList.remove('shepherd-hide-overlay');
        },
      },
    },
    [TourStepId.Final]: {
      id: TourStepId.Final,
      path: `/`,
      attachTo: {
        element: TourElementId.FinishModal,
      },
      requiresClickTarget: true,
      when: {
        show: () => {
          document
            .querySelector('.shepherd-element')
            ?.classList.add('shepherd-hide-element');
        },
        hide: () => {
          document
            .querySelector('.shepherd-element')
            ?.classList.remove('shepherd-hide-element');
        },
      },
    },
  };

  const stepMapper = (ogStep: ShepherdStep, index: number, stepsList: ShepherdStep[]) => {
    const step = { ...ogStep };

    // Navigate steps to the right page if they're not on it,
    // but allow them to have their own conditions.
    step.beforeShowPromise = () => {
      const { path, id } = step;
      const promises: Promise<any>[] = [];
      if (path) {
        promises.push(
          new Promise(resolve => {
            if (!path) return Promise.resolve();
            setTimeout(() => {
              if (window.location.pathname != path) {
                history.replace(`${path}?tourStep=${id}`);
              }
              setTimeout(resolve, 200);
            }, 50);
          }),
        );
      }
      if (ogStep.beforeShowPromise) {
        promises.push(ogStep.beforeShowPromise());
      }
      // This is an awful hack, but the modal hides and shows during
      // the duration of these promises which is pretty jarring. So we manually
      // force it to show between steps.
      // Any updates to the Shepherd library will probably break this code because
      // we're using the undocumented `modal` API.
      if (promises.length) {
        requestAnimationFrame(() => {
          if (!activeTour) return;
          (activeTour as any).modal?.show();
        });
      }

      return Promise.all(promises);
    };

    // Wrap tour step element IDs in selectors.
    if (step.attachTo?.element) {
      step.attachTo.element = `[data-tour-id="${step.attachTo.element}"]`;
    }
    if (step.advanceOn?.selector) {
      step.advanceOn.selector = `[data-tour-id="${step.advanceOn.selector}"]`;
    }

    // Steps that require clicking the target to advance must have clickable targets.
    step.canClickTarget = !!step.requiresClickTarget;

    // Add next and previous buttons, disable based on certain conditions.
    const isFirst = index === 0 || index === 1;
    const isLast = stepsList.length - 1 === index;
    step.buttons = [
      {
        text: 'Exit',
        action: activeTour?.cancel,
      },
      {
        text: '←',
        label: 'Previous',
        action: isFirst ? undefined : activeTour?.back,
        disabled: isFirst,
        classes: 'previous',
      },
      {
        text: '→',
        label: isLast ? 'Finish' : 'Next',
        action: step.requiresClickTarget ? flickerStepActionText : activeTour?.next,
        classes: 'next',
      },
    ];

    return step;
  };

  // Order steps in an array, and iterate over them to fill out calculable properties.
  const stepsList = [
    steps[TourStepId.Start],
    steps[TourStepId.VisitorsList],
    steps[TourStepId.ExampleVisitorCard],
    steps[TourStepId.VisitorDisplay],
    steps[TourStepId.StartCall],
    steps[TourStepId.StartTakeover],
    steps[TourStepId.EndCall],
    steps[TourStepId.Final],
  ].map(stepMapper);

  activeTour.addSteps(stepsList);

  // Add event handlers to sync with Redux.
  const handleEndTour = () => {
    // Update redux state & clear out local var
    dispatch(setIsTouring(false));
    activeTour = undefined;
    // Go back to home & clear search param.
    history.replace('/');
    // Kill any running camera stream.
    stopTourCameraStream();
  };
  activeTour.on('complete', handleEndTour);
  activeTour.on('cancel', handleEndTour);

  // Kick it off!
  if (initialStep) {
    const index = stepsList.findIndex(step => step.id === initialStep);
    if (index !== -1) {
      activeTour.show(index, true);
    }
  } else {
    activeTour.start();
  }
}

export async function endTour() {
  if (!activeTour) {
    return;
  }
  activeTour.cancel();
}

/**
 * Hides the currently active step's tooltip. Re-appears on step change.
 */
export function hideStepTooltip() {
  selectActiveShepherdElement()?.classList.remove('shepherd-enabled');
}

/**
 * Starts a user media stream for tour purposes.
 */
export async function setupTourCameraStream() {
  const { stream } = await mediaDevices.setupCameraMediaStream({
    audio: true,
    video: true,
  });
  tourCameraStream = stream;
  return tourCameraStream;
}

export function getTourCameraStream() {
  return tourCameraStream;
}

export function stopTourCameraStream() {
  if (tourCameraStream) {
    tourCameraStream.getTracks().forEach(t => t.stop());
  }
  tourCameraStream = null;
}

let tourCameraStream: MediaStream | null = null;

/**
 * Inserts a "X of Y" element between navigation buttons.
 */
const makeProgressIndicator = () => {
  // Taken from https://shepherdjs.dev/docs/tutorial-04-cookbook.html
  // Modified to be TS - friendly.
  if (!activeTour) return;
  const currentStep = activeTour.getCurrentStep();
  if (!currentStep) return;
  const currentStepElement = currentStep.getElement();
  if (!currentStepElement) return;
  const footerEl = currentStepElement.querySelector('.shepherd-footer');
  if (!footerEl) return;
  const secondBtnEl = currentStepElement.querySelector('.shepherd-button:first-child')
    ?.nextElementSibling;
  if (!secondBtnEl) return;
  const progressEl = document.createElement('span');
  progressEl.className = 'shepherd-progress';
  progressEl.innerText = `${activeTour.steps.indexOf(currentStep)} of ${
    activeTour.steps.length - 2
  }`;
  footerEl.insertBefore(progressEl, secondBtnEl);
};

/**
 * Flicks a class on for the active step to highlight the action they have to take.
 */
const flickerStepActionText = () => {
  const el = selectActiveShepherdElement();
  if (!el) return;
  el.classList.remove('shepherd-element--flickerText');
  requestAnimationFrame(() => el.classList.add('shepherd-element--flickerText'));
};

const selectActiveShepherdElement = () =>
  document.querySelector(
    '.shepherd-element.shepherd-enabled:not([data-popper-reference-hidden]):not([hidden])',
  );
